// Redis on the System overview: NextAdmin demo `/saas` metric card plus a compact table (`tables/basic-tables`)
// of the platform streams' backlog, read live.
import { useQuery } from '@tanstack/react-query';
import { Card, DataTable, type DataTableColumn, QueryBoundary, Skeleton } from '../../../components/ui/index.js';
import { getSystemRedisOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import type { SystemRedis } from '../../../generated/api/types.gen.js';
import { formatBytes, formatCount, formatRatio } from './metric-format.js';

type Stream = SystemRedis['streams'][number];

const STREAM_COLUMNS: DataTableColumn<Stream>[] = [
  { key: 'name', header: 'Stream', render: (s) => <span className="font-mono text-xs">{s.name}</span> },
  { key: 'length', header: 'Entries', align: 'right', render: (s) => formatCount(s.length) },
  {
    key: 'pending',
    header: 'Unacknowledged',
    align: 'right',
    render: (s) => formatCount(s.groups.reduce((sum, g) => sum + g.pending, 0)),
  },
  {
    key: 'lag',
    header: 'Not yet read',
    align: 'right',
    render: (s) => {
      const lags = s.groups.map((g) => g.lag).filter((lag): lag is number => lag !== null);
      return lags.length === 0 ? '—' : formatCount(Math.max(...lags));
    },
  },
];

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm font-medium text-heading">{value}</dd>
    </div>
  );
}

export function RedisCard() {
  const query = useQuery({ ...getSystemRedisOptions(), refetchInterval: 30_000 });
  return (
    <Card title="Redis" className="xl:col-span-12">
      <QueryBoundary query={query} label="Redis" loading={<Skeleton height={160} />}>
        {(redis) => {
          const server = redis.servers[0];
          return (
            <>
              {server ? (
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                  <Fact label="Memory used" value={formatBytes(server.usedMemoryBytes)} />
                  <Fact label="Peak" value={formatBytes(server.peakMemoryBytes)} />
                  <Fact
                    label="Commands"
                    value={server.commandsPerSecond === null ? '—' : `${formatCount(server.commandsPerSecond)}/s`}
                  />
                  <Fact label="Clients" value={formatCount(server.clients)} />
                  <Fact label="Hit ratio (1 h)" value={formatRatio(server.hitRatio, 1)} />
                </dl>
              ) : (
                <p className="text-sm text-muted">
                  No Redis numbers yet: the box that hosts Redis reports them once its agent runs with the Redis
                  receiver.
                </p>
              )}
              <h3 className="mt-5 mb-2 text-sm font-medium text-heading">Platform streams</h3>
              <DataTable
                columns={STREAM_COLUMNS}
                rows={redis.streams}
                rowKey={(s) => s.name}
                getRowAttention={(s) =>
                  s.groups.some((g) => (g.lag ?? 0) > 1_000 || g.pending > 1_000) ? 'warning' : undefined
                }
              />
            </>
          );
        }}
      </QueryBoundary>
    </Card>
  );
}
